import React from "react";
import {
  AlertOctagon,
  AlertTriangle,
  CheckCircle2,
  CircleDashed,
  Info,
  MinusCircle,
  ShieldQuestion,
} from "lucide-react";
import { cn } from "../../lib/utils";

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

export function fmtNumber(value: number | undefined, digits = 0): string | undefined {
  if (value === undefined) return undefined;
  return value.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  });
}

export function fmtPercent(value: number | undefined, digits = 1): string | undefined {
  if (value === undefined) return undefined;
  return `${value.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  })}%`;
}

export function fmtCurrency(
  value: number | undefined,
  currency = "USD"
): string | undefined {
  if (value === undefined) return undefined;
  return value.toLocaleString(undefined, {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  });
}

export function fmtDate(value: string | undefined): string | undefined {
  if (!value) return undefined;
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) return value;
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(
    new Date(timestamp)
  );
}

export function fmtDateTime(value: string | undefined): string | undefined {
  if (!value) return undefined;
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) return value;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(timestamp));
}

export function relativeTime(value: string | undefined): string | undefined {
  if (!value) return undefined;
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) return undefined;
  const deltaMs = Date.now() - timestamp;
  const minutes = Math.round(deltaMs / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  if (days < 60) return `${days} d ago`;
  const months = Math.round(days / 30);
  return `${months} mo ago`;
}

/** Freshness tone: how much to trust evidence of this age. */
export function freshnessTone(value: string | undefined): Tone {
  if (!value) return "neutral";
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) return "neutral";
  const days = (Date.now() - timestamp) / 86400000;
  if (days <= 7) return "success";
  if (days <= 30) return "warning";
  return "critical";
}

// ---------------------------------------------------------------------------
// Pills, badges, and evidence states
// ---------------------------------------------------------------------------

export type Tone =
  | "success"
  | "warning"
  | "critical"
  | "info"
  | "brand"
  | "neutral";

const TONE_CLASSES: Record<Tone, string> = {
  success: "bg-success/10 text-success ring-success/25",
  warning: "bg-warning/10 text-warning ring-warning/30",
  critical: "bg-critical/10 text-critical ring-critical/25",
  info: "bg-info/10 text-info ring-info/25",
  brand: "bg-brand/10 text-brand ring-brand/25",
  neutral: "bg-surface2 text-text2 ring-border/70",
};

export function Pill({
  tone = "neutral",
  icon,
  className,
  children,
}: {
  tone?: Tone;
  icon?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <span className={cn("asr-pill", TONE_CLASSES[tone], className)}>
      {icon}
      {children}
    </span>
  );
}

/** Map an arbitrary status string from the API to a presentation tone. */
export function statusTone(status: string | undefined): Tone {
  const normalized = (status || "").trim().toLowerCase();
  if (!normalized) return "neutral";
  if (
    /(fail|blocked|denied|gap|open — |critical|expired)/.test(normalized) ||
    normalized === "open"
  ) {
    return "critical";
  }
  if (/(review|partial|warn|conditional|pending|prototype|modeled|mixed)/.test(normalized)) {
    return "warning";
  }
  if (/(pass|demonstrated|measured|validated|complete|active|approved|satisfied|success|fixed|resolved|closed|ok)/.test(normalized)) {
    return "success";
  }
  if (/(accepted|suppressed|info)/.test(normalized)) return "info";
  return "neutral";
}

export function StatusPill({ status, demo }: { status?: string; demo?: boolean }) {
  if (!status) return <NotMeasured demo={demo} />;
  const tone = statusTone(status);
  const icon =
    tone === "success" ? (
      <CheckCircle2 className="h-3 w-3" aria-hidden="true" />
    ) : tone === "critical" ? (
      <AlertOctagon className="h-3 w-3" aria-hidden="true" />
    ) : tone === "warning" ? (
      <AlertTriangle className="h-3 w-3" aria-hidden="true" />
    ) : tone === "info" ? (
      <Info className="h-3 w-3" aria-hidden="true" />
    ) : (
      <MinusCircle className="h-3 w-3" aria-hidden="true" />
    );
  return (
    <Pill tone={tone} icon={icon}>
      {status}
    </Pill>
  );
}

/**
 * The canonical "we did not measure this" marker. Demo mode phrases it as a
 * validation gap; live mode states that the completed scan did not collect
 * the metric — evidence is never inferred in either mode.
 */
export function NotMeasured({ demo = false }: { demo?: boolean }) {
  return (
    <Pill
      tone="neutral"
      icon={<CircleDashed className="h-3 w-3" aria-hidden="true" />}
      className={demo ? undefined : "cursor-help"}
    >
      {demo ? (
        "Not measured — requires validation"
      ) : (
        <span title="This metric was not collected by the completed scan.">
          Not collected
        </span>
      )}
    </Pill>
  );
}

const DOMAIN_BADGES: Record<string, string> = {
  sast: "badge-sast",
  sca: "badge-sca",
  secrets: "badge-secret",
  secret: "badge-secret",
  iac: "badge-iac",
  license: "badge-license",
  container: "badge-container",
};

export function DomainBadge({ domain }: { domain: string }) {
  const cls = DOMAIN_BADGES[domain.toLowerCase()] || "badge bg-surface2 text-text2 ring-border/70";
  return <span className={cls}>{domain}</span>;
}

// ---------------------------------------------------------------------------
// Layout building blocks
// ---------------------------------------------------------------------------

export function SectionPanel({
  title,
  description,
  actions,
  children,
  className,
  bodyClassName,
  flush = false,
}: {
  title: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  bodyClassName?: string;
  /** flush: no body padding (tables that own their edges) */
  flush?: boolean;
}) {
  return (
    <section className={cn("asr-panel", className)}>
      <div className="asr-panel-head">
        <div className="min-w-0">
          <h3 className="flex items-center gap-2 text-[15px] font-semibold leading-6 text-text">
            <span
              aria-hidden="true"
              className="h-[14px] w-[3px] flex-none rounded-full bg-gradient-to-b from-brand to-focus/50"
            />
            <span className="min-w-0 truncate">{title}</span>
          </h3>
          {description && (
            <p className="mt-0.5 pl-[11px] text-[12.5px] leading-5 text-muted">{description}</p>
          )}
        </div>
        {actions && <div className="flex flex-none items-center gap-2">{actions}</div>}
      </div>
      <div className={cn(flush ? "" : "p-4", bodyClassName)}>{children}</div>
    </section>
  );
}

export function MetricTile({
  label,
  value,
  sub,
  tone = "neutral",
  demo,
}: {
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  tone?: Tone;
  demo?: boolean;
}) {
  const toneText: Record<Tone, string> = {
    success: "text-success",
    warning: "text-warning",
    critical: "text-critical",
    info: "text-info",
    brand: "text-brand",
    neutral: "text-text",
  };
  const toneAccent: Record<Tone, string> = {
    success: "bg-success/70",
    warning: "bg-warning/70",
    critical: "bg-critical/75",
    info: "bg-info/70",
    brand: "bg-brand/80",
    neutral: "bg-gradient-to-r from-brand/70 to-focus/30",
  };
  const missing = value === undefined || value === null || value === "";
  return (
    <div className="asr-tile min-w-0">
      <span aria-hidden="true" className={cn("absolute inset-x-0 top-0 h-[2px]", toneAccent[tone])} />
      <div className="truncate text-[11.5px] font-semibold uppercase tracking-[0.06em] text-muted">
        {label}
      </div>
      <div
        className={cn(
          "mt-1 text-[22px] font-bold leading-8 tracking-tight tabular-nums",
          toneText[tone]
        )}
      >
        {missing ? <NotMeasured demo={demo} /> : value}
      </div>
      {sub && <div className="mt-0.5 truncate text-xs text-muted">{sub}</div>}
    </div>
  );
}

export function EmptyPanel({
  title,
  detail,
  demo = false,
  icon,
}: {
  title?: string;
  detail?: string;
  demo?: boolean;
  icon?: React.ReactNode;
}) {
  return (
    <div className="asr-panel flex flex-col items-center justify-center border-dashed px-6 py-10 text-center">
      {icon || <ShieldQuestion className="h-7 w-7 text-muted" aria-hidden="true" />}
      <div className="mt-3 text-sm font-semibold text-text">
        {title || (demo ? "Requires validation" : "Not collected by the completed scan")}
      </div>
      <p className="mx-auto mt-1 max-w-md text-xs leading-5 text-muted">
        {detail ||
          (demo
            ? "The synthetic dataset does not include this evidence, so no conclusion is shown."
            : "This metric was not collected by the completed scan. Paxley never infers a result it did not measure.")}
      </p>
    </div>
  );
}

export function CaveatNote({
  children,
  tone = "info",
}: {
  children: React.ReactNode;
  tone?: Tone;
}) {
  const toneClasses: Record<Tone, string> = {
    info: "border-info/25 bg-info/5 text-text2",
    warning: "border-warning/30 bg-warning/10 text-text2",
    critical: "border-critical/25 bg-critical/5 text-text2",
    success: "border-success/25 bg-success/5 text-text2",
    brand: "border-brand/25 bg-brand/5 text-text2",
    neutral: "border-border/70 bg-surface2/60 text-text2",
  };
  return (
    <div className={cn("rounded-lg border px-3.5 py-2.5 text-xs leading-5", toneClasses[tone])}>
      {children}
    </div>
  );
}

export function KeyValueGrid({
  items,
  columns = 3,
  demo,
}: {
  items: { label: string; value?: React.ReactNode; mono?: boolean }[];
  columns?: 2 | 3 | 4;
  demo?: boolean;
}) {
  const visible = items.filter(Boolean);
  if (!visible.length) return null;
  const columnClasses = {
    2: "sm:grid-cols-2",
    3: "sm:grid-cols-2 xl:grid-cols-3",
    4: "sm:grid-cols-2 xl:grid-cols-4",
  } as const;
  return (
    <dl
      className={cn(
        "grid gap-px overflow-hidden rounded-lg border border-border/70 bg-border/50",
        columnClasses[columns]
      )}
    >
      {visible.map((item) => (
        <div key={item.label} className="min-w-0 bg-surface1 px-3.5 py-2.5">
          <dt className="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted">
            {item.label}
          </dt>
          <dd
            className={cn(
              "mt-1 break-words text-sm font-medium leading-5 text-text",
              item.mono && "font-mono text-xs"
            )}
          >
            {item.value === undefined || item.value === null || item.value === "" ? (
              <NotMeasured demo={demo} />
            ) : (
              item.value
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
}

// ---------------------------------------------------------------------------
// Charts (HTML/SVG, token-driven)
// ---------------------------------------------------------------------------

export interface BarListItem {
  label: React.ReactNode;
  value: number;
  /** Rendered right of the bar; defaults to the formatted value. */
  display?: React.ReactNode;
  colorClass?: string;
  onClick?: () => void;
}

/**
 * Labeled horizontal bar list. Identity comes from the row label, so adjacent
 * hue separation is not load-bearing.
 */
export function BarList({
  items,
  max,
  ariaLabel,
}: {
  items: BarListItem[];
  max?: number;
  ariaLabel?: string;
}) {
  const peak = max ?? Math.max(1, ...items.map((item) => item.value));
  return (
    <ul className="space-y-1.5" aria-label={ariaLabel}>
      {items.map((item, index) => {
        const width = Math.max(1.5, (item.value / peak) * 100);
        const row = (
          <>
            <span className="w-32 flex-none truncate text-[13px] font-medium text-text2 sm:w-44">
              {item.label}
            </span>
            <span className="relative h-[18px] min-w-0 flex-1 overflow-hidden rounded-[5px] bg-surface2/80 ring-1 ring-inset ring-border/40">
              <span
                className={cn(
                  "absolute inset-y-0 left-0 rounded-[5px] shadow-[inset_0_1px_0_rgba(255,255,255,0.14)] transition-[width] duration-500 ease-out",
                  item.colorClass || "bg-brand"
                )}
                style={{ width: `${width}%` }}
              />
            </span>
            <span className="w-14 flex-none text-right text-[13px] font-semibold tabular-nums text-text">
              {item.display ?? item.value.toLocaleString()}
            </span>
          </>
        );
        return (
          <li key={index}>
            {item.onClick ? (
              <button
                type="button"
                onClick={item.onClick}
                className="flex w-full items-center gap-3 rounded-md px-1 py-0.5 text-left hover:bg-surface2/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
              >
                {row}
              </button>
            ) : (
              <div className="flex items-center gap-3 px-1 py-0.5">{row}</div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

export interface Segment {
  label: string;
  value: number;
  colorClass: string;
}

/**
 * A 100% distribution strip with 2px gaps plus an always-visible legend, so
 * the strip reinforces labeled counts instead of carrying them alone.
 */
export function SegmentStrip({
  segments,
  ariaLabel,
}: {
  segments: Segment[];
  ariaLabel?: string;
}) {
  const visible = segments.filter((segment) => segment.value > 0);
  const total = visible.reduce((sum, segment) => sum + segment.value, 0);
  if (!total) return null;
  return (
    <div aria-label={ariaLabel}>
      <div className="flex h-2.5 w-full gap-[2px] overflow-hidden rounded-full">
        {visible.map((segment) => (
          <div
            key={segment.label}
            className={cn("h-full rounded-[2px]", segment.colorClass)}
            style={{ width: `${(segment.value / total) * 100}%` }}
          />
        ))}
      </div>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
        {visible.map((segment) => (
          <span key={segment.label} className="inline-flex items-center gap-1.5 text-[12.5px] text-text2">
            <span className={cn("h-2 w-2 rounded-[3px]", segment.colorClass)} aria-hidden="true" />
            {segment.label}
            <span className="font-semibold tabular-nums text-text">
              {segment.value.toLocaleString()}
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}

/** Compact linear meter with a visible value label. */
export function Meter({
  percent,
  tone = "brand",
  label,
}: {
  percent: number;
  tone?: Tone;
  label?: string;
}) {
  const clamped = Math.max(0, Math.min(100, percent));
  const toneBg: Record<Tone, string> = {
    success: "bg-success",
    warning: "bg-warning",
    critical: "bg-critical",
    info: "bg-info",
    brand: "bg-brand",
    neutral: "bg-muted",
  };
  return (
    <div
      className="flex items-center gap-3"
      role="meter"
      aria-valuenow={clamped}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
    >
      <div className="h-2 min-w-0 flex-1 overflow-hidden rounded-full bg-surface2">
        <div
          className={cn("h-full rounded-full transition-all duration-500", toneBg[tone])}
          style={{ width: `${clamped}%` }}
        />
      </div>
      <span className="flex-none text-sm font-semibold tabular-nums text-text">
        {clamped.toLocaleString(undefined, { maximumFractionDigits: 1 })}%
      </span>
    </div>
  );
}

/**
 * Dumbbell comparison: this application's value vs. a cohort median on a
 * shared scale. Good/bad tone comes from the metric's stated direction, and
 * both values are printed — color never carries the comparison alone.
 */
export function CompareBar({
  value,
  median,
  direction,
  format = (input: number) => input.toLocaleString(),
}: {
  value: number;
  median: number;
  direction?: string;
  format?: (input: number) => string;
}) {
  const peak = Math.max(value, median) * 1.15 || 1;
  const valuePct = (value / peak) * 100;
  const medianPct = (median / peak) * 100;
  const worse =
    direction === "lower-better" ? value > median : value < median;
  return (
    <div className="min-w-[160px]">
      <div className="relative h-3.5">
        <div className="absolute inset-x-0 top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-surface2" />
        <div
          className={cn(
            "absolute top-1/2 h-1.5 -translate-y-1/2 rounded-full",
            worse ? "bg-high" : "bg-success"
          )}
          style={{ width: `${Math.max(2, valuePct)}%` }}
        />
        <div
          className="absolute top-0 h-3.5 w-[2px] rounded bg-text/50"
          style={{ left: `${medianPct}%` }}
          title={`Cohort median ${format(median)}`}
        />
      </div>
      <div className="mt-1 flex justify-between text-[10.5px] leading-4 text-muted">
        <span>
          You{" "}
          <span className={cn("font-semibold tabular-nums", worse ? "text-high" : "text-success")}>
            {format(value)}
          </span>
        </span>
        <span>
          Median <span className="font-semibold tabular-nums text-text2">{format(median)}</span>
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Finding links
// ---------------------------------------------------------------------------

import { ArrowUpRight } from "lucide-react";
import type { AssuranceFinding } from "../../assurance/types";

const FINDING_LINK_TONES: Record<string, string> = {
  critical: "bg-critical/10 text-critical ring-critical/30 hover:bg-critical/20",
  high: "bg-high/10 text-high ring-high/30 hover:bg-high/20",
  medium: "bg-medium/10 text-medium ring-medium/30 hover:bg-medium/20",
  low: "bg-low/10 text-low ring-low/30 hover:bg-low/20",
  info: "bg-info/10 text-info ring-info/30 hover:bg-info/20",
};

/**
 * A finding id rendered as a real control. If the id resolves in the current
 * assessment it navigates to the full evidence view; if it does not, it is
 * explicitly labeled unavailable and rendered as inert text — never as a
 * dead control.
 */
export function FindingLink({
  id,
  finding,
  onOpen,
  className,
}: {
  id: string;
  finding?: AssuranceFinding;
  onOpen?: (finding: AssuranceFinding) => void;
  className?: string;
}) {
  if (finding && onOpen) {
    return (
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onOpen(finding);
        }}
        title={`${finding.title} — view full evidence`}
        className={cn(
          "asr-finding-link cursor-pointer",
          FINDING_LINK_TONES[finding.severity] || "bg-brand/10 text-brand ring-brand/30 hover:bg-brand/20",
          className
        )}
      >
        {id}
        <ArrowUpRight className="h-3 w-3 opacity-70" aria-hidden="true" />
      </button>
    );
  }
  return (
    <span
      title="Finding not available in this assessment"
      className={cn(
        "asr-finding-link bg-surface2/70 text-muted ring-border/60",
        className
      )}
    >
      {id}
    </span>
  );
}

/** Explicit evidence affordance for table rows (in addition to row click). */
export function ViewEvidenceButton({
  onClick,
  compact = false,
}: {
  onClick: () => void;
  compact?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
      className="btn-ghost whitespace-nowrap px-2 py-1 text-xs font-semibold text-brand hover:bg-brand/10"
    >
      {compact ? "Evidence" : "View full evidence"}
      <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
    </button>
  );
}
